<template>
    <dialog
        id="excentosModal"
        ref="dialog"
        class="modal"
    >
        <div class="modal-box max-w-none p-0 overflow-visible">
            <form method="dialog">
                <button type="submit" class="btn btn-sm btn-circle absolute top-[-16px] right-[-16px] z-10">
                    <MiscIcon name="x" />
                    <span class="sr-only">{{ $t('page.popup.close') }}</span>
                </button>
            </form>
            <div class="p-0 w-full max-w-none max-h-screen overflow-y-scroll">
                <div id="xc_application">
                    <div id="xc_application_pane" />
                    <div id="xc_loader" />
                </div>
            </div>
            <div
                v-if="loading"
                data-cy="excentos-modal-loading"
                class="absolute left-0 top-0 w-full h-full min-w-[300px] min-h-[400px] bg-base-100 flex items-center justify-center z-10"
            >
                <div class="loading" />
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button type="submit">{{ $t('page.popup.close') }}</button>
        </form>
    </dialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { useI18n, useNotification } from '#imports'

const { t } = useI18n()
const { showNotification } = useNotification()

const dialog = ref<HTMLDialogElement>()
const loading = ref(true)
let scriptElement: HTMLScriptElement | null = null
let loadedScriptSrc: string | null = null

onBeforeUnmount(() => {
    const excentosScripts = document.querySelectorAll('script[src*="excentos.com"]')
    excentosScripts.forEach((el) => {
        el.remove()
    })
})

/**
 * Opens the modal and loads the given Excentos script URL into it — the PDP's own advisor
 * URL, or (from the wishlist) a saved configuration's `backlink` to resume it. Reuses the
 * already-loaded script tag when reopening the same URL instead of re-injecting it, mirroring
 * the PDP's original re-open-without-reload behavior. Tracked via `loadedScriptSrc` rather
 * than reading the script tag's `src` back from the DOM — the browser resolves that to an
 * absolute URL, which isn't reliably string-equal to what was assigned, and one modal
 * instance (this component, shared by the whole wishlist list) opens a different URL per
 * item, so a same-vs-different-URL check needs to be exact, not best-effort.
 */
function open(scriptSrc: string) {
    loading.value = true
    dialog.value?.showModal()

    loadExcentosScript(
        scriptSrc,
        () => {
            loading.value = false
            if (window?.xcRender) {
                window.xcRender()
            }
        },
        () => {
            loading.value = false
            close()
            showNotification(t('product.detail.excentos.configuratorLoadError'), 'error', true)
        },
    )
}

function close() {
    dialog.value?.close()
}

function setLoading(value: boolean) {
    loading.value = value
}

function loadExcentosScript(src: string, onLoad: () => void, onError: () => void) {
    if (scriptElement != null && loadedScriptSrc === src) {
        onLoad()
        return
    }
    scriptElement?.remove()

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = src
    script.addEventListener('load', () => {
        loadedScriptSrc = src
        onLoad()
    }, false)
    script.addEventListener('error', () => {
        script.remove()
        if (scriptElement === script) {
            scriptElement = null
        }
        onError()
    }, false)

    scriptElement = script
    document.head.appendChild(script)
}

defineExpose({ open, close, setLoading })
</script>
